import React, { useMemo, useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { useSpotify } from '../hooks/useSpotify';
import { useMusicBrainz } from '../hooks/useMusicBrainz';
import SpotifyEmbed from './SpotifyEmbed';

const CassetteReel = ({ className = '' }) => (
  <svg className={`cassette-reel ${className}`} viewBox="0 0 64 64" aria-hidden="true">
    <circle cx="32" cy="32" r="31" fill="#111111" />
    <g fill="#ffffff">
      {[0, 60, 120, 180, 240, 300].map((deg) => (
        <rect
          key={deg}
          x="27"
          y="8"
          width="10"
          height="16"
          rx="1.5"
          transform={`rotate(${deg} 32 32)`}
        />
      ))}
      <circle cx="32" cy="32" r="12" />
    </g>
    <circle cx="32" cy="32" r="5.5" fill="#111111" />
  </svg>
);

const CassetteBody = ({
  face,
  queuedLabel,
  peekMeta,
  isCueing,
  isGroup,
  onToggleExpand,
  onToggleView,
}) => {
  const isA = face === 'A';
  const sideMeta = isCueing
    ? 'Fast forwarding'
    : isA
      ? peekMeta
      : (peekMeta ? `${peekMeta} · Side B` : 'Side B');

  return (
    <div className={`cassette-shell ${isA ? 'is-front' : 'is-back'}`}>
      <span className="cassette-screw cassette-screw-tl" aria-hidden="true"></span>
      <span className="cassette-screw cassette-screw-tr" aria-hidden="true"></span>
      <span className="cassette-screw cassette-screw-bl" aria-hidden="true"></span>
      <span className="cassette-screw cassette-screw-br" aria-hidden="true"></span>

      <span className="cassette-grab" aria-hidden="true"></span>

      <div className="cassette-label">
        <button
          type="button"
          className="cassette-face"
          onClick={onToggleExpand}
          disabled={isCueing}
          aria-label={`Open details for ${queuedLabel}`}
        >
          <div className="cassette-title">
            <div className="cassette-title-copy">
              <h2>
                <span className="cassette-name">{queuedLabel}</span>
                {isCueing && <span className="cue-dots" aria-hidden="true"></span>}
              </h2>
              {sideMeta && <p className="cassette-meta">{sideMeta}</p>}
            </div>
          </div>
        </button>

        {isGroup ? (
          <button
            type="button"
            className="cassette-tape"
            onClick={() => onToggleView(!isA)}
            disabled={isCueing}
            aria-label={isA ? 'Flip to Side B, related groups' : 'Flip to Side A, members'}
          >
            <div className="cassette-window-row">
              <CassetteReel className="reel-left" />
              <span className="cassette-tape-window">
                <span className="tape-pack tape-pack-left"></span>
                <span className="tape-ribbon"></span>
                <span className="tape-pack tape-pack-right"></span>
              </span>
              <CassetteReel className="reel-right" />
            </div>
          </button>
        ) : (
          <div className="cassette-window-row" aria-hidden="true">
            <CassetteReel className="reel-left" />
            <span className="cassette-tape-window">
              <span className="tape-pack tape-pack-left"></span>
              <span className="tape-ribbon"></span>
              <span className="tape-pack tape-pack-right"></span>
            </span>
            <CassetteReel className="reel-right" />
          </div>
        )}

        {isGroup && (
          <div className="cassette-sides" role="tablist" aria-label="Tape side">
            <button
              type="button"
              className={`cassette-side ${isA ? 'is-active' : ''}`}
              aria-pressed={isA}
              disabled={isCueing}
              onClick={(event) => {
                event.stopPropagation();
                onToggleView(true);
              }}
            >
              <span className="cassette-side-tag">Side A</span>
              <span className="cassette-side-name">Members</span>
            </button>
            <button
              type="button"
              className={`cassette-side ${!isA ? 'is-active' : ''}`}
              aria-pressed={!isA}
              disabled={isCueing}
              onClick={(event) => {
                event.stopPropagation();
                onToggleView(false);
              }}
            >
              <span className="cassette-side-tag">Side B</span>
              <span className="cassette-side-name">Related groups</span>
            </button>
          </div>
        )}
      </div>

      <div className="cassette-foot" aria-hidden="true">
        <span className="foot-pin"></span>
        <span className="foot-capstan foot-capstan-l"></span>
        <span className="foot-slot foot-slot-l"></span>
        <span className="foot-slot foot-slot-r"></span>
        <span className="foot-capstan foot-capstan-r"></span>
      </div>
    </div>
  );
};

const SWIPE_OPEN = -72;
const SWIPE_CLOSE = 72;

const useSheetSwipe = ({ allowMouse, expanded, onToggle, locked }) => {
  const session = useRef(null);
  const swallow = useRef(false);
  const [shift, setShift] = useState(0);
  const [dragging, setDragging] = useState(false);

  const onPointerDown = useCallback((event) => {
    if (locked) return;
    if (event.pointerType === 'mouse' && !allowMouse) return;
    if (event.target.closest('button.cassette-side, button.cassette-tape, .spotify-embed-slot, a, iframe')) {
      return;
    }
    const body = event.target.closest('.sheet-body');
    if (body && body.scrollTop > 2) return;
    session.current = {
      id: event.pointerId,
      y: event.clientY,
      x: event.clientX,
      armed: false,
    };
  }, [allowMouse, locked]);

  const onPointerMove = useCallback((event) => {
    const current = session.current;
    if (!current || current.id !== event.pointerId) return;
    const dy = event.clientY - current.y;
    const dx = event.clientX - current.x;
    if (!current.armed) {
      if (Math.hypot(dx, dy) < 10) return;
      if (Math.abs(dx) > Math.abs(dy)) {
        session.current = null;
        return;
      }
      if (expanded && dy < 8) {
        session.current = null;
        return;
      }
      if (!expanded && dy > -8) {
        session.current = null;
        return;
      }
      current.armed = true;
      setDragging(true);
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch (error) {
        /* capture is best-effort */
      }
    }
    setShift(expanded ? Math.max(0, dy) : Math.min(0, dy));
  }, [expanded]);

  const finish = useCallback((event) => {
    const current = session.current;
    if (!current || current.id !== event.pointerId) return;
    const dy = event.clientY - current.y;
    const armed = current.armed;
    const cancelled = event.type === 'pointercancel';
    session.current = null;
    setDragging(false);
    setShift(0);
    if (!armed || cancelled) return;
    if ((expanded && dy > SWIPE_CLOSE) || (!expanded && dy < SWIPE_OPEN)) {
      swallow.current = true;
      onToggle();
    }
  }, [expanded, onToggle]);

  const onToggleSafe = useCallback((event) => {
    if (swallow.current) {
      swallow.current = false;
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      return;
    }
    onToggle();
  }, [onToggle]);

  return {
    shift,
    dragging,
    onToggleSafe,
    swipe: {
      onPointerDown,
      onPointerMove,
      onPointerUp: finish,
      onPointerCancel: finish,
    },
  };
};

const GenreChips = ({ genres }) => {
  const rowRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [overflows, setOverflows] = useState(false);

  useLayoutEffect(() => {
    const node = rowRef.current;
    if (!node) return undefined;
    if (open) {
      setOverflows(true);
      return undefined;
    }
    const check = () => {
      setOverflows(node.scrollHeight > node.clientHeight + 1);
    };
    check();
    const observer = new ResizeObserver(check);
    observer.observe(node);
    return () => observer.disconnect();
  }, [genres, open]);

  if (!genres.length) return null;

  return (
    <div className={`genre-row ${open ? 'is-open' : ''}`}>
      <div ref={rowRef} className={`genre-chips ${open ? 'is-open' : ''}`}>
        {genres.map((genre) => (
          <span key={genre.name} className="genre-chip">{genre.name}</span>
        ))}
      </div>
      {(overflows || open) && (
        <button
          type="button"
          className="genre-more"
          onClick={() => setOpen((current) => !current)}
        >
          {open ? 'Less' : 'More'}
        </button>
      )}
    </div>
  );
};

const ArtistDetails = ({ artist, spotifyArtistName, isExpanded, onToggleExpand, isMobile, onMemberClick, isArtistsView, onToggleView, isCueing = false, cueingName = '', cueingId = '' }) => {
  const { spotifyId, error, isCloseMatch, isLoading: isLoadingSpotify, refetch } = useSpotify(spotifyArtistName);

  const [showInfoBox, setShowInfoBox] = useState(true);
  const [genres, setGenres] = useState([]);
  const [showRetry, setShowRetry] = useState(false);

  const { getArtistDetails } = useMusicBrainz();
  const { shift, dragging, onToggleSafe, swipe } = useSheetSwipe({
    allowMouse: Boolean(isMobile),
    locked: isCueing,
    expanded: isExpanded,
    onToggle: onToggleExpand,
  });
  const dragStyle = dragging || shift
    ? { transform: `translateY(${shift}px)`, transition: 'none' }
    : undefined;

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      if (error) {
        console.error('Spotify data loading error:', error);
      } else if (!spotifyId) {
        console.log('No Spotify data found for artist:', artist.name);
      }
    }
  }, [spotifyId, error, artist.name]);

  const handleRetry = useCallback(() => {
    setShowRetry(false);
    refetch();
  }, [refetch]);

  useEffect(() => {
    const fetchArtistDetails = async () => {
      if (artist.id) {
        try {
          const details = await getArtistDetails(artist.id);
          if (details && details.genres) {
            setGenres(details.genres);
          } else {
            console.error('No genres found in artist details');
            setGenres([]);
          }
        } catch (error) {
          console.error('Error fetching artist details:', error);
          setGenres([]);
        }
        setShowInfoBox(true);
        setShowRetry(false);
      }
    };

    fetchArtistDetails();
  }, [artist.id, getArtistDetails]);

  useEffect(() => {
    if (!isLoadingSpotify) {
      setShowRetry(false);
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      setShowRetry(true);
    }, 8000);

    return () => clearTimeout(timeoutId);
  }, [isLoadingSpotify, spotifyArtistName]);

  useEffect(() => {
    if (!isExpanded) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onToggleExpand();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isExpanded, onToggleExpand]);

  const formatDate = (begin, end) => {
    const formatYear = (date) => date ? new Date(date).getFullYear() : null;
    const beginYear = formatYear(begin);
    const endYear = formatYear(end);

    if (beginYear && endYear) {
      return `${beginYear} - ${endYear}`;
    } else if (beginYear) {
      return `${beginYear} - present`;
    } else if (endYear) {
      return `? - ${endYear}`;
    }
    return '';
  };

  const groupedRelations = useMemo(() => {
    if (!artist || !artist.relations) return {};

    const memberRelations = artist.relations.filter(rel => 
      rel.type === 'member of band' || rel.type === 'member'
    );

    return memberRelations.reduce((acc, rel) => {
      const name = rel.artist?.name || rel.target?.name || 'Unknown';
      const id = rel.artist?.id || rel.target?.id;
      if (!acc[id]) {
        acc[id] = { name, id, roles: {}, isOriginal: false, mostRecentYear: 0 };
      }
      
      const roles = (rel.attributes || []).filter(attr => attr !== 'original').map(attr => attr.split(' (')[0]);
      const dateRange = formatDate(rel.begin, rel.end);
      const endYear = rel.end ? new Date(rel.end).getFullYear() : new Date().getFullYear();
      
      if (!acc[id].roles[dateRange]) {
        acc[id].roles[dateRange] = [];
      }
      acc[id].roles[dateRange].push(...roles);
      
      if ((rel.attributes || []).includes('original')) {
        acc[id].isOriginal = true;
      }
      
      acc[id].mostRecentYear = Math.max(acc[id].mostRecentYear, endYear);
      
      return acc;
    }, {});
  }, [artist]);

  const sortedMembers = useMemo(() => {
    return Object.values(groupedRelations).sort((a, b) => {
      const rolePriority = (member) => {
        const roles = Object.values(member.roles).flat();
        if (roles.some(role => role.toLowerCase().includes('lead vocals'))) return 1;
        if (roles.some(role => role.toLowerCase().includes('vocals'))) return 2;
        if (roles.some(role => role.toLowerCase().includes('guitar'))) return 3;
        return 4;
      };

      const priorityA = rolePriority(a);
      const priorityB = rolePriority(b);

      if (a.isOriginal !== b.isOriginal) {
        return a.isOriginal ? -1 : 1;
      }

      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }

      return b.mostRecentYear - a.mostRecentYear;
    });
  }, [groupedRelations]);

  const handleMemberClick = useCallback((memberId, name) => {
    if (isCueing) return;
    if (!isArtistsView && artist.type === 'Group') {
      onMemberClick(memberId, false, name);
    } else {
      onMemberClick(memberId, true, name);
    }
  }, [onMemberClick, isArtistsView, artist.type, isCueing]);

  const lifeSpan = formatDate(artist?.['life-span']?.begin, artist?.['life-span']?.end);
  const rosterCount = sortedMembers.length;

  const relatedGroups = useMemo(() => {
    if (!artist || artist.type !== 'Group' || !artist.relations) return [];

    const members = artist.relations.filter(rel => rel.type === 'member of band');
    const groupsWithMembers = members.reduce((acc, member) => {
      if (member.artist && member.artist.relations) {
        member.artist.relations
          .filter(rel => rel.type === 'member of band' && rel.artist && rel.artist.id !== artist.id)
          .forEach(groupRel => {
            if (groupRel.artist && groupRel.artist.id) {
              if (!acc[groupRel.artist.id]) {
                acc[groupRel.artist.id] = {
                  name: groupRel.artist.name,
                  id: groupRel.artist.id,
                  members: new Map()
                };
              }
              const memberKey = `${member.artist.id}-${groupRel.artist.id}`;
              acc[groupRel.artist.id].members.set(memberKey, {
                id: member.artist.id,
                name: member.artist.name
              });
            }
          });
      }
      return acc;
    }, {});

    return Object.values(groupsWithMembers)
      .map(group => ({
        ...group,
        count: group.members.size,
        members: Array.from(group.members.values())
      }))
      .sort((a, b) => b.count - a.count);
  }, [artist]);

  const relatedCount = relatedGroups.length;
  const queuedLabel = useMemo(() => {
    if (!isCueing) return artist.name;
    if (cueingName) return cueingName;
    const fromLineup = sortedMembers.find((member) => member.id === cueingId);
    if (fromLineup?.name) return fromLineup.name;
    const fromGroups = relatedGroups.find((group) => group.id === cueingId);
    if (fromGroups?.name) return fromGroups.name;
    for (const group of relatedGroups) {
      const shared = group.members.find((member) => member.id === cueingId);
      if (shared?.name) return shared.name;
    }
    return 'Loading';
  }, [isCueing, cueingName, cueingId, artist.name, sortedMembers, relatedGroups]);
  const peekMeta = artist?.type === 'Group'
    ? (isArtistsView
      ? `Side A · ${rosterCount} member${rosterCount !== 1 ? 's' : ''}`
      : `Side B · ${relatedCount} related group${relatedCount !== 1 ? 's' : ''}`)
    : [
        lifeSpan,
        artist?.country,
        rosterCount > 0 ? `${rosterCount} group${rosterCount !== 1 ? 's' : ''}` : null,
      ].filter(Boolean).join(' · ');

  const renderTabContent = () => {
    if (artist.type !== 'Group') {
      return renderRelationships();
    }

    return isArtistsView ? renderRelationships() : renderRelatedGroups();
  };

  if (!artist) return null;

  const renderMetadata = () => (
    <div className="artist-metadata">
      <p>
        <strong>Life span:</strong> <span className="metadata-value">{lifeSpan}</span>
      </p>
      <p><strong>Country:</strong> <span className="metadata-value">{artist.country || 'Unknown'}</span></p>
    </div>
  );

  const renderDisambiguation = () => (
    artist.disambiguation ? (
      <p className="disambiguation">{artist.disambiguation}</p>
    ) : null
  );

  const renderSpotifyPlayer = () => {
    if (isLoadingSpotify) {
      return (
        <div className="spotify-loading">
        <span className="loading-text">
          Loading Spotify data
        </span>
        {showRetry && (
          <button onClick={handleRetry} className="spotify-retry">
            Retry
          </button>
        )}
      </div>
      );
    }

    if (showInfoBox) {
      return (
        <div className="spotify-miss">
          <p>Couldn’t find them on Spotify.</p>
          <button
            type="button"
            onClick={() => setShowInfoBox(false)}
            className="spotify-dismiss"
          >
            Hide
          </button>
        </div>
      );
    }
    return null;
  };

  const renderGenres = () => <GenreChips key={artist.id} genres={genres} />;

  const renderRelationships = () => {
    if (sortedMembers.length === 0) {
      return <p className="sheet-list-empty">No members listed.</p>;
    }

    return (
      <div className="artist-relationships">
        <ul>
          {sortedMembers.map((member, index) => (
            <li key={member.id} className="member-item">
              <button
                type="button"
                className="member-hit"
                onClick={() => handleMemberClick(member.id, member.name)}
              >
                <span className="track-index">{String(index + 1).padStart(2, '0')}</span>
                <span className="member-name">{member.name}</span>
                {member.isOriginal && <span className="original-label">Original</span>}
                {Object.entries(member.roles).map(([dateRange, roles], roleIndex) => (
                  <div key={roleIndex} className="role-info">
                    <span className="roles">{roles.join(', ')}</span>
                    <span className="date-range">{dateRange}</span>
                  </div>
                ))}
              </button>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const renderRelatedGroups = () => {
    if (relatedGroups.length === 0) {
      return <p className="sheet-list-empty">No related groups yet.</p>;
    }

    return (
      <div className="artist-relationships">
        <ul>
          {relatedGroups.map((group, index) => (
            <li key={group.id} className="member-item">
              <button
                type="button"
                className="member-hit"
                onClick={() => handleMemberClick(group.id, group.name)}
              >
                <span className="track-index">{String(index + 1).padStart(2, '0')}</span>
                <span className="member-name">{group.name}</span>
                <span className="shared-members-count">
                  ({group.count} shared member{group.count !== 1 ? 's' : ''})
                </span>
              </button>
              <div className="shared-members-list">
                {group.members.map((member) => (
                  <button
                    key={`${group.id}-${member.id}`}
                    type="button"
                    className="shared-member-link"
                    onClick={() => handleMemberClick(member.id, member.name)}
                  >
                    {member.name}
                  </button>
                ))}
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <>
      {!isExpanded && (
        <div
          className={`artist-details-modal cassette contracted sheet-dock ${isMobile ? 'mobile' : ''} ${isCueing ? 'is-cueing' : ''} ${!isArtistsView ? 'is-side-b' : ''} ${dragging ? 'is-dragging' : ''}`}
          style={dragStyle}
          {...swipe}
        >
          <div className="sheet-pull" aria-hidden="true"></div>
          {artist.type === 'Group' ? (
            <div className="cassette-flip-scene">
              <div className={`cassette-flip-card ${!isArtistsView ? 'is-flipped' : ''}`}>
                <CassetteBody
                  face="A"
                  queuedLabel={queuedLabel}
                  peekMeta={peekMeta}
                  isCueing={isCueing}
                  isGroup
                  onToggleExpand={onToggleSafe}
                  onToggleView={onToggleView}
                />
                <CassetteBody
                  face="B"
                  queuedLabel={queuedLabel}
                  peekMeta={peekMeta}
                  isCueing={isCueing}
                  isGroup
                  onToggleExpand={onToggleSafe}
                  onToggleView={onToggleView}
                />
              </div>
            </div>
          ) : (
            <CassetteBody
              face="A"
              queuedLabel={queuedLabel}
              peekMeta={peekMeta}
              isCueing={isCueing}
              isGroup={false}
              onToggleExpand={onToggleSafe}
              onToggleView={onToggleView}
            />
          )}
        </div>
      )}

      {isExpanded && (
        <div
          className={`sheet-dock ${isMobile ? 'mobile' : ''} ${dragging ? 'is-dragging' : ''}`}
          style={dragStyle}
          {...swipe}
        >
          <div className="sheet-pull" aria-hidden="true"></div>
          <div className={`artist-details-sheet ${isMobile ? 'mobile' : ''}`}>
          <div className="sheet-lip" aria-hidden="true">
            <span className="sheet-grab"></span>
          </div>
          <header className="sheet-header">
            <h2>{artist.name}</h2>
          </header>
          {artist.type === 'Group' && (
            <div className="sheet-sides" role="tablist" aria-label="Tape side">
              <button
                type="button"
                role="tab"
                aria-selected={isArtistsView}
                className={`cassette-side ${isArtistsView ? 'is-active' : ''}`}
                onClick={() => onToggleView(true)}
              >
                <span className="cassette-side-tag">Side A</span>
                <span className="cassette-side-name">Members</span>
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={!isArtistsView}
                className={`cassette-side ${!isArtistsView ? 'is-active' : ''}`}
                onClick={() => onToggleView(false)}
              >
                <span className="cassette-side-tag">Side B</span>
                <span className="cassette-side-name">Related groups</span>
              </button>
            </div>
          )}
          <div className="sheet-body">
            {spotifyId && isCloseMatch ? (
              <SpotifyEmbed spotifyId={spotifyId} active={!isCueing} />
            ) : (
              renderSpotifyPlayer()
            )}
            {renderDisambiguation()}
            {renderGenres()}
            {renderMetadata()}
            <div className="sheet-list-head">
              <p className="sheet-list-title">
                {artist.type === 'Group'
                  ? (isArtistsView ? 'Members' : 'Related groups')
                  : 'Lineup'}
              </p>
              <p className="sheet-list-count">
                {artist.type === 'Group'
                  ? (isArtistsView
                    ? `${rosterCount} on the roster`
                    : `${relatedCount} with shared members`)
                  : `${rosterCount} group${rosterCount !== 1 ? 's' : ''}`}
              </p>
            </div>
            {renderTabContent()}
          </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ArtistDetails;
